// What each field enricher reaches for, so the UI can state the cost of a run
// BEFORE it starts rather than after the invoice.
//
// Two buckets (docs/plans/2026-07-10-company-linkedin-llm-picker-design.md):
//   Deterministic Enrichment — code only, or derived from data we already hold. Free.
//   AI-Powered Enrichment    — reaches a third party (Serper and/or an LLM). Paid.
//
// The `deterministic` provider blanks both API keys, so EVERY enricher is free under
// it. Some still do useful work there (company_country's ccTLD tier, company_linkedin's
// first-party JSON-LD probe); the rest skip.

// ids mirror buildFieldEnricher in cmd/pipelinecontrol/runner.go — an unknown name is a 400.
export const ENRICHERS = [
  {
    id: 'company_linkedin',
    label: 'Company - LinkedIn',
    entity: 'companies',
    serper: true,
    llm: true,
    note: 'A LinkedIn link on the company’s own site is used for free. Otherwise the model picks among search results.'
  },
  {
    id: 'company_country',
    label: 'Company - Country',
    entity: 'companies',
    serper: true,
    llm: true,
    note: 'A country read off the website’s domain ending (.co.th → Thailand) is free. The rest go to the model.'
  },
  {
    id: 'company_city',
    label: 'Company - City',
    entity: 'companies',
    serper: true,
    llm: true,
    note: 'Searches for the company’s headquarters, then the model reads the city off it. No HQ found means no guess — the field is left empty.'
  },
  {
    id: 'company_industry',
    label: 'Company - Industry',
    entity: 'companies',
    serper: false,
    llm: true,
    note: 'Classified by the model. No web search.'
  },
  {
    id: 'people_linkedin',
    label: 'People - LinkedIn',
    entity: 'people',
    serper: true,
    llm: true,
    note: 'Searches for each profile, then the model verifies it belongs to that person.'
  },
  {
    id: 'people_location',
    label: 'People - Location',
    entity: 'people',
    serper: false,
    llm: true,
    note: 'Inferred by the model from what we already hold. No web search.'
  }
];

const byId = Object.fromEntries(ENRICHERS.map((e) => [e.id, e]));

export function enricherById(id) {
  return byId[id];
}

// spendOf reports what a run of this enricher will call. Unknown ids fail CLOSED —
// we never tell someone "no spend" about something we don't recognise.
export function spendOf(id, provider) {
  if (provider === 'deterministic') {
    return { spends: false, serper: false, llm: false };
  }
  const e = byId[id];
  if (!e) {
    return { spends: true, serper: true, llm: true };
  }
  return { spends: true, serper: e.serper, llm: e.llm };
}

// spendSummary is the sentence shown next to the button. It names the providers that
// will be called and the bound on how many records they'll be called for.
export function spendSummary(id, provider, limit) {
  const { spends, serper, llm } = spendOf(id, provider);
  const e = byId[id];
  const entity = e ? e.entity : 'records';

  if (!spends) {
    return 'No spend — this provider makes no external calls.';
  }

  const calls = [serper && 'Serper', llm && 'an LLM'].filter(Boolean);
  const called = calls.length === 2 ? `${calls[0]} and ${calls[1]}` : calls[0];

  const n = String(limit ?? '').trim();
  const bound = n === '' ? `every remaining ${entity} record` : `up to ${n} ${entity}`;

  return `Calls ${called} for ${bound}. This costs money.`;
}
